import axiosClient from "./apiClient";

/**
 * Full UserConfig payload the API exposes at
 *   PUT / GET /api/sensors/{name}/user-config
 *
 * Mirrors `cts_api/src/Models/DTO/UserConfigDto.cs`. Every field is
 * optional / nullable: partial-update semantics are the whole point.
 * When a client PUTs a subset, the server preserves the omitted fields
 * (both in DynamoDB and in the MQTT publish body to the device).
 *
 * The phone app (see `lib/services/cts_api/cts_api_service.dart`
 * `putUserConfig`) uses the same shape end-to-end; the dashboard bulk
 * modal and the per-sensor drawer both go through here so the two
 * clients stay in lockstep.
 *
 * Notes on individual fields:
 *   - `ambientAlerts`: home-temperature comfort range in Celsius. The UI
 *     layer (see homeTemperatureThreshold.ts) trades this for a
 *     more form-friendly {enabled,minC,maxC} shape.
 *   - `unattendedSpeed`: "fast" | "default" | "slow" | "off". Off opts
 *     out of the unattended safety alert entirely — the phone app
 *     confirms before letting a user pick it, and this dashboard editor
 *     should too.
 *   - `speakerVolume`: 0..100.
 *   - `buttonLockout`: physical-button tamper lockout. When true, the
 *     10 s / 30 s button holds route to the in-app BLE-admin recovery
 *     flow instead of directly re-provisioning or factory-resetting.
 *
 * Demo Mode is intentionally NOT here -- it is install-time only over
 * SoftAP Action 29 on the sensor, not exposed over the cloud API.
 */
export type UnattendedSpeed = "fast" | "default" | "slow" | "off";

export type AmbientAlertsDto = {
  enabled: boolean;
  lowC: number;
  highC: number;
};

export type UserConfigDto = {
  // `| null` like its five siblings: the API sets
  // DefaultIgnoreCondition = JsonIgnoreCondition.Never, so it omits nothing
  // and a never-configured sensor answers with every key explicitly null.
  // Without this, `version !== undefined` type-checks and is true for a
  // value the server really sends.
  version?: number | null;
  ambientAlerts?: AmbientAlertsDto | null;
  unattendedSpeed?: UnattendedSpeed | null;
  auxLights?: boolean | null;
  speakerVolume?: number | null;
  buttonLockout?: boolean | null;
};

/**
 * Fetch the sensor's current UserConfig. Returns null when NEITHER the
 * cloud nor the device holds anything — callers should treat null as
 * "use defaults" and offer the editor's own defaults.
 *
 * "Neither", not "never saved": since cts_api#56 this read reconciles the
 * device's last reported config onto the stored row (REQ-DEV-9a), so a
 * sensor configured over softAP at install returns the DEVICE's values on
 * the first GET. The advice above is unchanged; the reason for it is.
 *
 * THIS GET HAS A SIDE EFFECT AND IS NOT CHEAP. It runs a paginated query
 * over the device's whole event history — the index ranges on
 * serial_number, so every row for the device shares one range key — and,
 * when it adopts, writes the stored row back. The bulk editor fans this
 * out per sensor, uncached (see the cache-buster below). Worth weighing
 * before adding a call site; cts_api has a filed follow-up for a
 * created_at GSI that would make it a single-row read.
 */
export async function getUserConfig(
  name: string
): Promise<UserConfigDto | null> {
  try {
    const res = await axiosClient.get<UserConfigDto>(
      `sensors/${name}/user-config`,
      // Cache-buster: this GET drives editor initial state, and a stale
      // 304 defeats the user's most recent write. Since cts_api#56 it does
      // more -- a cached response never reaches the API, so the read-path
      // repair never runs, and a bundle wiped by cts_api#47 stays wiped.
      { params: { t: Date.now() } }
    );
    // `?? null` only fires on null/undefined, and the empty bundle is an
    // OBJECT. The API omits nothing, so a never-configured sensor -- the
    // documented normal first-open, cts_api REQ-DEV-5 -- answers 200 with
    // six explicit nulls, which is thoroughly truthy. Returning that made
    // the contract above false for the most common case it names.
    const data = res.data;
    if (!data) return null;
    // Checked field by field rather than with Object.values(): this response
    // may grow keys -- cts_api#60 proposes a driftedFields array on this very
    // endpoint -- and one non-config key that is always present would make
    // every empty bundle look populated, silently restoring the bug above.
    const hasAnyValue =
      data.version != null ||
      data.ambientAlerts != null ||
      data.unattendedSpeed != null ||
      data.auxLights != null ||
      data.speakerVolume != null ||
      data.buttonLockout != null;
    return hasAnyValue ? data : null;
  } catch (err) {
    const status = (err as { response?: { status?: number } })?.response
      ?.status;
    if (status === 404) return null;
    throw err;
  }
}

/**
 * Partial-update PUT. Only include the fields the user actually edited
 * -- the server merges into the persisted row and forwards the same
 * partial to the device. Fields omitted from `patch` are preserved on
 * BOTH sides.
 *
 * Resolves on the server's 200 (row updated + MQTT publish accepted).
 * A non-200 bubbles up so callers can surface a per-sensor failure.
 * We deliberately do not poll for the device's own ack here: the bulk
 * modal fans out across many sensors and adding a 5 s poll per sensor
 * makes a 40-unit property painful.
 *
 * The trade is that a device which FAILS to apply the command is not
 * surfaced here. Nothing the API returns TODAY surfaces it -- but the
 * read-path reconcile already DETECTS it: cts_api#56 assembles a per-field
 * `drift` list of exactly "the device reports something other than what we
 * sent" and sends it only to a server-side log. Returning that would give
 * per-sensor confirmation with no poll, no req_id and no second round trip.
 * Tracked as cts_api#60.
 *
 * The API's read-path reconcile (REQ-DEV-9a, cts_api#56) repairs a cloud row
 * that holds NOTHING for a field -- after a softAP install, or a bundle wiped
 * by cts_api#47. Where the cloud already has a value and the device reports a
 * different one, the CLOUD value wins and the difference is logged server-side
 * where no browser sees it. That is deliberate: a device that was offline when
 * a PUT landed must not revert the user's change on reconnect.
 *
 * Which means the adopt branch is unreachable from HERE, by construction:
 * PutUserConfig persists the bundle and THEN publishes, so by the time an ack
 * could arrive the cloud holds a value for every field we just set. A
 * subsequent GET therefore returns WHAT WE SENT -- not confirmation the device
 * took it.
 *
 * So what makes skipping the poll acceptable is the product decision above --
 * a 40-unit bulk edit does not warrant per-sensor confirmation -- and not any
 * property of the API. If it is ever required, surfacing `drift` is the
 * cheaper route; the poll is the fallback, not the only option.
 *
 * (The claim this replaces -- "A subsequent GET reflects the applied value
 * once the device acks" -- is what is in `user-config` and `main` today, so a
 * reader who remembers it gets an explanation. It is false on this path, for
 * the reason above.)
 */
export async function putUserConfig(
  name: string,
  patch: UserConfigDto
): Promise<void> {
  const body: UserConfigDto = { version: 1, ...patch };
  await axiosClient.put(`sensors/${name}/user-config`, body);
}
